import { useEffect, useState, useMemo, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { 
  ArrowLeft, 
  Clock, 
  Dumbbell,
  Loader2,
  ChevronDown,
  Play as PlayIcon
} from "lucide-react";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { getTemplateMeta, assignTemplateToUser, type WorkoutTemplateMeta } from "@/integrations/backend/workout-templates";
import { fetchTemplateGroupings, type UIGrouping, type UIExercise } from "@/integrations/backend/workouts";
import { listExercises, type ClientExerciseVM } from "@/integrations/backend/exercises";
import { listEquipment, type Equipment } from "@/integrations/backend/equipment";
import { getExerciseCardImageSrc, getVideoEmbedUrl } from "@/lib/exercise-media";
import { toast } from "sonner";
import { useEnforceLegalApproval } from "@/hooks/use-enforce-legal";
import { useIsMobile } from "@/hooks/use-mobile";
import { ClientNavSpacer } from "@/components/client/ClientNavSpacer";
import { useAuth } from "@/hooks/useAuth";
import { DesktopSidebar } from "@/components/DesktopSidebar";
import { CATALOG_STALE_BASE_MS, staleTimeWithJitterFromKey } from "@/lib/query-cache";
import { queryKeys } from "@/lib/query-keys";

function withAutoplay(embedUrl: string): string {
  // Add autoplay params in a URL-safe way. Also set muted to satisfy autoplay policies.
  try {
    const u = new URL(embedUrl);

    // YouTube
    if (u.hostname.includes("youtube.com") && u.pathname.includes("/embed/")) {
      u.searchParams.set("autoplay", "1");
      u.searchParams.set("mute", "1");
      u.searchParams.set("playsinline", "1");
      return u.toString();
    }

    // Vimeo
    if (u.hostname.includes("vimeo.com") && u.pathname.includes("/video/")) {
      u.searchParams.set("autoplay", "1");
      u.searchParams.set("muted", "1");
      return u.toString();
    }

    // Fallback: just add autoplay=1
    u.searchParams.set("autoplay", "1");
    return u.toString();
  } catch {
    // If it's not a valid URL for some reason, do a minimal fallback.
    return embedUrl.includes("?") ? `${embedUrl}&autoplay=1` : `${embedUrl}?autoplay=1`;
  }
}

// Helper component for video/image with play button
const ExerciseMedia = ({
  exerciseName,
  imageUrl,
  videoUrl,
}: {
  exerciseName: string;
  imageUrl?: string | null;
  videoUrl?: string | null;
}) => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const embedUrl = getVideoEmbedUrl(videoUrl);
  const autoplayEmbedUrl = embedUrl ? withAutoplay(embedUrl) : null;
  const imageSrc = getExerciseCardImageSrc({
    name: exerciseName,
    image_url: imageUrl,
    video_url: videoUrl,
  });

  // If video is playing, show video player
  if (isVideoPlaying && autoplayEmbedUrl) {
    return (
      <div className="relative">
        <AspectRatio ratio={16 / 9} className="bg-dark-slate rounded-lg overflow-hidden">
          <iframe
            src={autoplayEmbedUrl}
            title="Exercise video"
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen
          />
        </AspectRatio>
      </div>
    );
  }

  // Always show a thumbnail image (image → video thumbnail → default fallback), matching Exercise Library behavior.
  return (
    <div className="relative">
      <AspectRatio ratio={16 / 9} className="bg-dark-slate rounded-lg overflow-hidden flex items-center justify-center">
        <img
          src={imageSrc}
          alt={`${exerciseName} demonstration`}
          className="object-cover w-full h-full"
          loading="lazy"
        />
        {videoUrl ? (
          embedUrl ? (
            <button
              onClick={() => setIsVideoPlaying(true)}
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors rounded-lg cursor-pointer"
              aria-label="Play exercise video"
            >
              <div className="bg-primary rounded-full p-4 hover:scale-110 transition-transform">
                <PlayIcon className="h-8 w-8 text-black fill-black" />
              </div>
            </button>
          ) : (
            <a
              href={videoUrl}
              target="_blank"
              rel="noreferrer"
              className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors rounded-lg"
              aria-label="Open exercise video"
            >
              <div className="bg-primary rounded-full p-4 hover:scale-110 transition-transform">
                <PlayIcon className="h-8 w-8 text-black fill-black" />
              </div>
            </a>
          )
        ) : null}
      </AspectRatio>
    </div>
  );
};

const ViewWorkoutTemplate = () => {
  useEnforceLegalApproval();
  const { templateId } = useParams<{ templateId: string }>();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { profile } = useAuth();
  
  const [isStartingWorkout, setIsStartingWorkout] = useState(false);

  const metaKey = templateId ? queryKeys.workoutTemplates.meta(templateId) : (["workout-templates", "meta", ""] as const);
  const metaQuery = useQuery({
    queryKey: metaKey,
    queryFn: () => getTemplateMeta(templateId!),
    enabled: Boolean(templateId),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...metaKey]),
  });
  const groupingsKey = templateId ? queryKeys.workoutTemplates.groupings(templateId) : (["workout-templates", "groupings", ""] as const);
  const groupingsQuery = useQuery({
    queryKey: groupingsKey,
    queryFn: () => fetchTemplateGroupings(templateId!),
    enabled: Boolean(templateId),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...groupingsKey]),
  });
  const exercisesKey = queryKeys.exercises.clientList;
  const exercisesQuery = useQuery({
    queryKey: exercisesKey,
    queryFn: listExercises,
    enabled: Boolean(templateId),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...exercisesKey]),
  });
  const equipmentKey = queryKeys.equipment.all;
  const equipmentQuery = useQuery({
    queryKey: equipmentKey,
    queryFn: listEquipment,
    enabled: Boolean(templateId),
    staleTime: staleTimeWithJitterFromKey(CATALOG_STALE_BASE_MS, [...equipmentKey]),
  });

  const templateMeta = metaQuery.data ?? null;
  const groupings = groupingsQuery.data ?? [];
  const equipmentList = equipmentQuery.data ?? [];

  const exercises = useMemo(() => {
    const all = exercisesQuery.data ?? [];
    const groups = groupingsQuery.data ?? [];
    const exerciseIds = new Set<string>();
    groups.forEach((group) => {
      group.exercises.forEach((ex) => {
        if (ex.exercise) exerciseIds.add(ex.exercise);
      });
    });
    if (exerciseIds.size === 0) return [];
    return all.filter((ex) => exerciseIds.has(ex.id));
  }, [exercisesQuery.data, groupingsQuery.data]);

  const queryErr =
    metaQuery.error ?? groupingsQuery.error ?? exercisesQuery.error ?? equipmentQuery.error;
  const error = !templateId
    ? "Template ID is required"
    : queryErr
      ? queryErr instanceof Error
        ? queryErr.message
        : String(queryErr)
      : null;
  const isLoading =
    Boolean(templateId) &&
    (metaQuery.isPending || groupingsQuery.isPending || exercisesQuery.isPending || equipmentQuery.isPending);

  // Create exercise ID to exercise details map
  const exerciseMap = useMemo(() => {
    const map: Record<string, ClientExerciseVM> = {};
    exercises.forEach(ex => {
      map[ex.id] = ex;
    });
    return map;
  }, [exercises]);

  // Create equipment name to equipment details map
  const equipmentMap = useMemo(() => {
    const map: Record<string, Equipment> = {};
    equipmentList.forEach(eq => {
      map[eq.name] = eq;
    });
    return map;
  }, [equipmentList]);

  const loadErrorToastRef = useRef<unknown>(null);
  useEffect(() => {
    if (!templateId || !queryErr) {
      loadErrorToastRef.current = null;
      return;
    }
    if (loadErrorToastRef.current === queryErr) return;
    loadErrorToastRef.current = queryErr;
    const message =
      queryErr instanceof Error ? queryErr.message : "Failed to load workout template";
    toast.error("Could not load workout", { description: message });
  }, [templateId, queryErr]);

  // Calculate total exercises
  const totalExercises = useMemo(() => {
    return groupings.reduce((sum, group) => sum + group.exercises.length, 0);
  }, [groupings]);

  // Get difficulty color
  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case "Beginner": return "bg-strength text-white";
      case "Intermediate": return "bg-mobility text-black";
      case "Advanced": return "bg-cardio text-white";
      case "Expert": return "bg-cardio text-white";
      case "Pro": return "bg-cardio text-white";
      default: return "bg-dark-slate text-white";
    }
  };

  // Format duration
  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (remainingSeconds === 0) return `${minutes}min`;
    return `${minutes}min ${remainingSeconds}s`;
  };

  // Handle start workout
  const handleStartWorkout = async () => {
    if (!templateId || !profile?.id) {
      toast.error("Unable to start workout", { description: "Missing template or profile information" });
      return;
    }

    try {
      setIsStartingWorkout(true);
      // Assign template to user for today
      const assignedId = await assignTemplateToUser(templateId, profile.id, new Date());
      // Navigate to workout session
      navigate(`/workout-session?assigned=${encodeURIComponent(assignedId)}`);
    } catch (err: unknown) {
      toast.error("Could not start workout", { description: err instanceof Error ? err.message : String(err) });
    } finally {
      setIsStartingWorkout(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen gradient-dark flex items-center justify-center">
        <div className="text-center text-white">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-primary" />
          <p>Loading workout template...</p>
        </div>
      </div>
    );
  }

  if (error || !templateMeta) {
    return (
      <div className="min-h-screen gradient-dark flex items-center justify-center">
        <div className="text-center text-white max-w-md mx-auto px-4">
          <p className="text-lg font-medium mb-4">Error loading workout</p>
          <p className="text-gray-light mb-6">{error || "Template not found"}</p>
          <Button onClick={() => navigate("/explore")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Explore
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen gradient-dark">
      <DesktopSidebar activeTab="explore" />
      
      {/* Header */}
      <div className={`bg-dark-card border-b border-primary/20 ${!isMobile ? "md:ml-16" : ""}`}>
        <div className="max-w-4xl mx-auto px-4 py-4 text-white">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate("/explore?tab=workouts")}
              className="text-white hover:bg-white/10"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
              Back
            </Button>
            <Button
              onClick={handleStartWorkout}
              disabled={isStartingWorkout}
              className="gradient-primary text-black hover:opacity-90"
            >
              {isStartingWorkout ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Starting...
                </>
              ) : (
                <>
                  <PlayIcon className="w-4 h-4 mr-2" />
                  Start Workout
                </>
              )}
            </Button>
          </div>
          
          <div className="mb-4">
            <h1 className="text-2xl font-bold mb-3">{templateMeta.name}</h1>
            
            <div className="flex flex-wrap items-center gap-3 mb-3">
              {templateMeta.duration_minutes && (
                <Badge variant="outline" className="border-primary/30 text-white bg-dark-slate/50">
                  <Clock className="w-3 h-3 mr-1" />
                  {templateMeta.duration_minutes} min
                </Badge>
              )}
              
              {templateMeta.difficulty_counts && (
                <>
                  {(["Beginner", "Intermediate", "Expert", "Pro"] as const)
                    .map((lvl) => ({ lvl, n: templateMeta.difficulty_counts?.[lvl] ?? 0 }))
                    .filter(({ n }) => n > 0)
                    .map(({ lvl, n }) => (
                      <Badge
                        key={lvl}
                        variant="secondary"
                        className={getDifficultyColor(lvl)}
                      >
                        {lvl}: {n}
                      </Badge>
                    ))}
                </>
              )}
              
              <Badge variant="outline" className="border-primary/30 text-white bg-dark-slate/50">
                <Dumbbell className="w-3 h-3 mr-1" />
                {totalExercises} exercises
              </Badge>
            </div>

            {templateMeta.primary_muscles_top && templateMeta.primary_muscles_top.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {templateMeta.primary_muscles_top.slice(0, 5).map((muscle) => (
                  <Badge
                    key={muscle}
                    variant="outline"
                    className="text-xs border-primary/30 text-gray-light bg-dark-slate/50"
                  >
                    {muscle}
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={`max-w-4xl mx-auto px-4 pt-6 ${!isMobile ? "pb-6 md:ml-16" : ""}`}>
        {groupings.length === 0 ? (
          <Card className="bg-dark-card border border-primary/20">
            <CardContent className="p-6 text-center text-white">
              <p>This workout template has no exercises yet.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {groupings.map((grouping, groupIndex) => {
              const isSingleExercise = grouping.exercises.length === 1;
              const isSuperset = grouping.exercises.length > 1;

              // Render exercise component
              const renderExercise = (workoutExercise: UIExercise, exIndex: number) => {
                const exerciseDetails = exerciseMap[workoutExercise.exercise];
                
                if (!exerciseDetails) {
                  return (
                    <div key={workoutExercise.id} className="text-gray-light text-sm">
                      Exercise not found (ID: {workoutExercise.exercise})
                    </div>
                  );
                }

                // Defensive: some older/partial exercise payloads can be missing array fields.
                const primaryMuscles = exerciseDetails.primary_muscles ?? [];
                const secondaryMuscles = exerciseDetails.secondary_muscles ?? [];
                const muscleActions = exerciseDetails.muscle_actions ?? [];
                const equipment = exerciseDetails.equipment ?? [];

                return (
                  <div key={workoutExercise.id} className={isSuperset && exIndex > 0 ? "mt-6" : ""}>
                    {isSuperset && exIndex > 0 && (
                      <div className="h-px bg-primary/20 mb-6" />
                    )}
                    
                    <div className="space-y-4">
                      {/* Exercise Name and Difficulty */}
                      <div>
                        <h3 className="text-xl font-bold text-white mb-2">
                          {exerciseDetails.name}
                        </h3>
                        {workoutExercise.difficulty && (
                          <Badge
                            variant="secondary"
                            className={getDifficultyColor(workoutExercise.difficulty)}
                          >
                            {workoutExercise.difficulty}
                          </Badge>
                        )}
                      </div>

                      {/* Exercise Media */}
                      <ExerciseMedia 
                        exerciseName={exerciseDetails.name}
                        imageUrl={exerciseDetails.image_url}
                        videoUrl={exerciseDetails.video_url}
                      />

                      {/* Workout Parameters */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {workoutExercise.sets > 0 && (
                          <div className="bg-dark-slate p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-light mb-1">Sets</p>
                            <p className="font-bold text-white">{workoutExercise.sets}</p>
                          </div>
                        )}
                        
                        {workoutExercise.reps > 0 && (
                          <div className="bg-dark-slate p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-light mb-1">Reps</p>
                            <p className="font-bold text-white">{workoutExercise.reps}</p>
                          </div>
                        )}
                        
                        {workoutExercise.time_seconds > 0 && (
                          <div className="bg-dark-slate p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-light mb-1">Time</p>
                            <p className="font-bold text-white">
                              {formatDuration(workoutExercise.time_seconds)}
                            </p>
                          </div>
                        )}
                        
                        {workoutExercise.rest_seconds > 0 && (
                          <div className="bg-dark-slate p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-light mb-1">Rest</p>
                            <p className="font-bold text-white">
                              {formatDuration(workoutExercise.rest_seconds)}
                            </p>
                          </div>
                        )}
                        
                        {(workoutExercise.weight_lbs > 0 || workoutExercise.weight_kg > 0) && (
                          <div className="bg-dark-slate p-3 rounded-lg text-center">
                            <p className="text-xs text-gray-light mb-1">Weight</p>
                            <p className="font-bold text-white">
                              {workoutExercise.weight_lbs > 0 
                                ? `${workoutExercise.weight_lbs} lbs`
                                : `${workoutExercise.weight_kg} kg`}
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Exercise Details Accordion */}
                      <Accordion type="single" collapsible className="w-full">
                        {exerciseDetails.instructions && (
                          <AccordionItem value={`instructions-${workoutExercise.id}`} className="border-primary/20">
                            <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg text-white hover:no-underline">
                              <span className="flex items-center">
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Exercise Instructions
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg text-sm text-gray-light">
                              {exerciseDetails.instructions}
                            </AccordionContent>
                          </AccordionItem>
                        )}

                        {exerciseDetails.tips ? (
                          <AccordionItem value={`tips-${workoutExercise.id}`} className="border-primary/20">
                            <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg text-white hover:no-underline">
                              <span className="flex items-center">
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Tips
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg text-sm text-gray-light whitespace-pre-wrap">
                              {exerciseDetails.tips}
                            </AccordionContent>
                          </AccordionItem>
                        ) : null}

                        {exerciseDetails.programming_use ? (
                          <AccordionItem value={`programming-${workoutExercise.id}`} className="border-primary/20">
                            <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg text-white hover:no-underline">
                              <span className="flex items-center">
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Programming Notes
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg text-sm text-gray-light whitespace-pre-wrap">
                              {exerciseDetails.programming_use}
                            </AccordionContent>
                          </AccordionItem>
                        ) : null}
                        
                        {(primaryMuscles.length > 0 || secondaryMuscles.length > 0 || muscleActions.length > 0) && (
                          <AccordionItem value={`muscles-${workoutExercise.id}`} className="border-primary/20">
                            <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg text-white hover:no-underline">
                              <span className="flex items-center">
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Muscle Groups
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg">
                              <div className="space-y-3">
                                {primaryMuscles.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-light mb-2">Primary Muscles:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {primaryMuscles.map((muscle) => (
                                        <Badge
                                          key={muscle}
                                          variant="outline"
                                          className="text-xs border-primary/30 text-white bg-primary/20"
                                        >
                                          {muscle}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {secondaryMuscles.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-light mb-2">Secondary Muscles:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {secondaryMuscles.map((muscle) => (
                                        <Badge
                                          key={muscle}
                                          variant="outline"
                                          className="text-xs border-primary/30 text-gray-light bg-dark-card"
                                        >
                                          {muscle}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {muscleActions.length > 0 && (
                                  <div>
                                    <p className="text-xs text-gray-light mb-2">Muscle Actions:</p>
                                    <div className="flex flex-wrap gap-2">
                                      {muscleActions.map((action) => (
                                        <Badge
                                          key={action}
                                          variant="outline"
                                          className="text-xs border-primary/30 text-gray-light bg-dark-card"
                                        >
                                          {action}
                                        </Badge>
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                        
                        {equipment.length > 0 && (
                          <AccordionItem value={`equipment-${workoutExercise.id}`} className="border-primary/20">
                            <AccordionTrigger className="py-3 px-4 bg-dark-slate rounded-lg text-white hover:no-underline">
                              <span className="flex items-center">
                                <ChevronDown className="w-4 h-4 mr-2" />
                                Equipment Needed
                              </span>
                            </AccordionTrigger>
                            <AccordionContent className="bg-dark-slate p-4 mt-2 rounded-lg">
                              <div className="space-y-3">
                                <div className="flex flex-wrap gap-2">
                                  {equipment.map((eqName) => {
                                    const equipmentDetails = equipmentMap[eqName];
                                    return (
                                      <Badge
                                        key={eqName}
                                        variant="outline"
                                        className="text-xs border-primary/30 text-white bg-dark-card"
                                      >
                                        {eqName}
                                      </Badge>
                                    );
                                  })}
                                </div>
                                
                                {/* Equipment Instructions */}
                                {equipment.some((eqName) => equipmentMap[eqName]?.instructions) && (
                                  <div className="mt-3 space-y-2">
                                    {equipment.map((eqName) => {
                                      const equipmentDetails = equipmentMap[eqName];
                                      if (!equipmentDetails?.instructions) return null;
                                      return (
                                        <div key={eqName} className="text-xs text-gray-light">
                                          <p className="font-medium text-white mb-1">{eqName}:</p>
                                          <p>{equipmentDetails.instructions}</p>
                                        </div>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            </AccordionContent>
                          </AccordionItem>
                        )}
                      </Accordion>
                    </div>
                  </div>
                );
              };

              // For single exercise groups, render without grouping card
              if (isSingleExercise) {
                return (
                  <Card key={grouping.id} className="bg-dark-card border border-primary/20 overflow-hidden">
                    <CardContent className="p-4">
                      {renderExercise(grouping.exercises[0], 0)}
                    </CardContent>
                  </Card>
                );
              }

              // For supersets, render with grouping card
              return (
                <Card key={grouping.id} className="bg-dark-card border border-primary/20 overflow-hidden">
                  <CardHeader className="gradient-primary p-4">
                    <h2 className="text-black text-lg font-bold">
                      Superset
                    </h2>
                  </CardHeader>
                  
                  <CardContent className="p-4">
                    {grouping.exercises.map((workoutExercise, exIndex) => 
                      renderExercise(workoutExercise, exIndex)
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
        {isMobile && <ClientNavSpacer />}
      </div>

    </div>
  );
};

export default ViewWorkoutTemplate;

